import { useWebgate } from '@xvia/webgate-connect';
import { isAxiosError } from 'axios';
import { useCallback, useState } from 'react';

import { useSearchProfile } from '@hooks/profile/useSearchProfile';
import { useUpdateProfile } from '@hooks/profile/useUpdateProfile';

import { Profile, type ProfilePageStatus } from './Profile';

import type { Profile as ProfileData, ProfileUpdate } from '@services/profile/profile.types';

function describeSearchError(err: unknown): string {
  if (isAxiosError(err)) {
    if (err.response?.status === 404) {
      return 'Cidadão não encontrado. Verifique o CPF informado.';
    }
    if (err.response?.status === 403) {
      return 'Acesso negado. Você não tem permissão para acessar esta ferramenta.';
    }
  }
  return 'Erro ao carregar o perfil. Tente novamente mais tarde.';
}

export const ProfileContainer = () => {
  const { accessToken } = useWebgate();
  const token = accessToken ?? '';

  const [status, setStatus] = useState<ProfilePageStatus>('search');
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [pageError, setPageError] = useState('');
  const [currentCpf, setCurrentCpf] = useState('');

  const searchProfile = useSearchProfile(token);
  const updateProfile = useUpdateProfile(token);

  const handleSearch = useCallback(
    (cpf: string) => {
      setStatus('loading');
      setPageError('');
      setCurrentCpf(cpf);

      searchProfile.mutate(cpf, {
        onSuccess: (found) => {
          setProfile(found);
          setStatus('ready');
        },
        onError: (err) => {
          setPageError(describeSearchError(err));
          setStatus('error');
        },
      });
    },
    [searchProfile],
  );

  const handleSave = useCallback(
    async (update: ProfileUpdate) => {
      const updated = await updateProfile.mutateAsync({ cpf: currentCpf, payload: update });

      setProfile((prev) => ({
        ...prev,
        ...updated,
        cpf: updated.cpf ?? prev?.cpf ?? currentCpf,
      }));
    },
    [updateProfile, currentCpf],
  );

  const handleBack = useCallback(() => {
    setStatus('search');
    setProfile(null);
    setCurrentCpf('');
    setPageError('');
  }, []);

  return (
    <Profile
      status={status}
      profile={profile}
      pageError={pageError}
      isSearching={searchProfile.isPending}
      onSearch={handleSearch}
      onSave={handleSave}
      onBack={handleBack}
    />
  );
};
